import { handleError, hasError, tryCatch, unknownError } from '@repo/shared/api/helpers'
import { SudoContextCaseWorkerDTO } from '@repo/shared/dtos/caseWorker'
import { SudoRoutes } from '@repo/shared/routes-sudo'
import { AxiosInstance } from 'axios'

import { buildSudoContextUserFromDto } from '@/external/api/helpers'
import { mockedData } from '@/store/mock/mock'
import { ISudoApi } from '@/store/spi/sudo'

type T = {
  sudo: ISudoApi['users']
}

export const apiUsers: (api?: AxiosInstance) => T = (api): T => ({
  //
  // SUDO
  //
  sudo: {
    createOne: api
      ? async (p) => {
          const res = await tryCatch(
            async () =>
              await api.post<SudoRoutes.users.createOne.Response>(
                SudoRoutes.users.createOne.call(),
                p satisfies SudoRoutes.users.createOne.Request,
              ),
          )
          if (hasError(res)) return handleError(res)
          return res.users.map(buildSudoContextUserFromDto)
        }
      : (p) => {
          // @ts-expect-error mock
          delete p.fields.password
          return Promise.resolve([
            mockedData.sudo.user,
            {
              ...p.fields,
              id: 'newUser',
              disabled: false,
            },
          ])
        },

    enableOne: api
      ? async ({ userId }) => {
          const res = await tryCatch(
            async () =>
              await api.patch<SudoRoutes.users.enableOne.Response>(
                SudoRoutes.users.enableOne.call({ userId }),
              ),
          )
          if (hasError(res)) return handleError(res)
          return buildSudoContextUserFromDto(res.user)
        }
      : (p) => {
          if (p.userId === mockedData.sudo.user.id) {
            return Promise.resolve({
              ...mockedData.sudo.user,
              disabled: false,
            })
          }
          return Promise.resolve(unknownError)
        },

    disableOne: api
      ? async ({ userId }) => {
          const res = await tryCatch(
            async () =>
              await api.patch<SudoRoutes.users.disableOne.Response>(
                SudoRoutes.users.disableOne.call({ userId }),
              ),
          )
          if (hasError(res)) return handleError(res)
          return buildSudoContextUserFromDto(res.user)
        }
      : (p) => {
          if (p.userId === mockedData.sudo.user.id) {
            return Promise.resolve({
              ...mockedData.sudo.user,
              disabled: true,
            })
          }
          return Promise.resolve(unknownError)
        },

    patchOne: api
      ? async ({ fields, userId }) => {
          const res = await tryCatch(
            async () =>
              await api.patch<SudoRoutes.users.patchOne.Response>(
                SudoRoutes.users.patchOne.call({ userId }),
                { fields } satisfies SudoRoutes.users.patchOne.Request,
              ),
          )
          if (hasError(res)) return handleError(res)
          return buildSudoContextUserFromDto(res.user)
        }
      : (p) => {
          return Promise.resolve({
            ...mockedData.sudo.user,
            id: p.userId,
            ...p.fields,
          })
        },

    patchOnePassword: api
      ? async ({ password, userId }) => {
          await tryCatch(
            async () =>
              await api.patch<SudoRoutes.users.patchOnePassword.Response>(
                SudoRoutes.users.patchOnePassword.call({ userId }),
                {
                  password,
                } satisfies SudoRoutes.users.patchOnePassword.Request,
              ),
          )
          return true
        }
      : () => {
          return Promise.resolve(true)
        },

    getCaseWorkersFromOne: api
      ? async ({ userId }) => {
          const res = await tryCatch(
            async () =>
              await api.get<SudoRoutes.users.getCaseWorkersFromOne.Response>(
                SudoRoutes.users.getCaseWorkersFromOne.call({ userId }),
              ),
          )
          if (hasError(res)) return handleError(res)
          return res.caseWorkers.map((cw) => ({
            firstName: cw.firstName,
            id: cw.id,
            lastName: cw.lastName,
            organizationId: cw.organizationId,
            role: cw.role,
            status: cw.status,
          }))
        }
      : () => {
          return Promise.resolve(
            mockedData.caseWorkers.map(
              (cw) =>
                ({
                  id: cw.id,
                  firstName: cw.firstName,
                  lastName: cw.lastName,
                  organizationId: 'org1',
                  role: cw.role,
                  status: cw.status,
                }) satisfies SudoContextCaseWorkerDTO,
            ),
          )
        },
  },
})
